/**
 * Money — integer cents, NZD.
 *
 * Floating point is banned throughout this codebase. A cent lost to rounding in a trust
 * account is a compliance failure, not a rounding error. Everything here is `bigint`.
 *
 * See docs/TRUST_ACCOUNTING.md §4.
 */

/** Integer cents. Always NZD. Never construct one by casting a float. */
export type Money = bigint;

export const ZERO: Money = 0n;

/** NZ GST rate, as a ratio. 15% → tax = inclusive * 3/23, exclusive * 3/20. */
export const GST_NUMERATOR = 3n;
export const GST_INCLUSIVE_DENOMINATOR = 23n;
export const GST_EXCLUSIVE_DENOMINATOR = 20n;

/**
 * The same rate as a percentage, for labels: `15`.
 *
 * DERIVED, never typed out. A screen that says "15%" while the maths uses another ratio is a
 * screen that lies about money, and NZ has changed this rate twice — 12.5% to 15% in 2010.
 */
export const GST_RATE_PERCENT = Number((GST_NUMERATOR * 100n) / GST_EXCLUSIVE_DENOMINATOR);

/**
 * Parse a user-entered amount ("1,234.50", "1234.5", "$1234") into cents.
 * Rejects anything with more than two decimal places rather than silently rounding —
 * a third decimal place means the caller's assumption is wrong and we want to know.
 */
export function parseAmount(input: string): Money {
  const cleaned = input.trim().replace(/[$,\s]/g, '');
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) {
    throw new RangeError(`Not a valid NZD amount: ${JSON.stringify(input)}`);
  }
  const [, sign, whole, fraction = ''] = match;
  // Unreachable: group 2 is not optional in the pattern above. Stated as a check rather than
  // a `!` because the alternative is telling the compiler to be quiet inside a money parser,
  // and the day someone edits that regex this is what catches it.
  if (whole === undefined) throw new RangeError(`Not a valid NZD amount: ${JSON.stringify(input)}`);

  const cents = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return sign ? -cents : cents;
}

/**
 * Read a share out of an `allocate()` result.
 *
 * `allocate` returns exactly one share per weight, so every index a caller asks for exists —
 * but the compiler cannot see that, and `shares[0]!` on a GST line is the compiler telling you
 * it cannot prove a tax figure exists and being told to be quiet. A missing share means
 * `allocate` is broken; throwing says so, where a `?? 0n` would quietly bank a wrong number.
 */
function share(shares: readonly Money[], index: number): Money {
  const value = shares[index];
  if (value === undefined) {
    throw new RangeError(`allocate() produced no share at index ${index}`);
  }
  return value;
}

/** Format cents for display: 123456n → "1,234.56". Sign is the caller's business. */
export function formatAmount(cents: Money, opts: { signed?: boolean } = {}): string {
  const negative = cents < ZERO;
  const abs = negative ? -cents : cents;
  const whole = abs / 100n;
  const fraction = (abs % 100n).toString().padStart(2, '0');
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = `${grouped}.${fraction}`;
  if (!negative) return opts.signed ? `+${body}` : body;
  return `-${body}`;
}

/**
 * Accounting presentation: negatives in parentheses. Used on financial reports,
 * where a minus sign is easy to miss and the convention is parentheses.
 */
export function formatAccounting(cents: Money): string {
  return cents < ZERO ? `(${formatAmount(-cents)})` : formatAmount(cents);
}

/**
 * Split `total` across `weights` so the parts always sum exactly to `total`.
 *
 * This is the only correct way to divide money in this codebase. Rounding each share
 * independently loses or invents cents; the largest-remainder method distributes the
 * leftover deterministically, so the same inputs always produce the same split.
 *
 * Used for: multi-owner rent splits (basis points), GST components, fee apportionment.
 *
 * @param weights non-negative; e.g. [5000n, 3000n, 2000n] for a 50/30/20 split
 */
export function allocate(total: Money, weights: readonly bigint[]): Money[] {
  if (weights.length === 0) {
    throw new RangeError('allocate() requires at least one weight');
  }
  if (weights.some((w) => w < 0n)) {
    throw new RangeError('allocate() weights must be non-negative');
  }

  const totalWeight = weights.reduce((a, b) => a + b, 0n);
  if (totalWeight === 0n) {
    throw new RangeError('allocate() weights must not sum to zero');
  }

  // Work on the magnitude so truncation behaves symmetrically for negative totals.
  const negative = total < ZERO;
  const magnitude = negative ? -total : total;

  const shares = weights.map((w) => (magnitude * w) / totalWeight);
  const distributed = shares.reduce((a, b) => a + b, ZERO);
  let remainder = magnitude - distributed;

  // Hand the leftover cents to the largest fractional remainders, ties broken by index
  // so the result is stable and reproducible.
  const order = weights
    .map((w, i) => ({ i, frac: (magnitude * w) % totalWeight }))
    .sort((a, b) => (b.frac === a.frac ? a.i - b.i : b.frac > a.frac ? 1 : -1));

  for (const { i } of order) {
    if (remainder <= 0n) break;
    shares[i] = share(shares, i) + 1n;
    remainder -= 1n;
  }

  return negative ? shares.map((s) => -s) : shares;
}

/** GST component of a GST-inclusive amount. inclusive * 3 / 23, remainder to the tax line. */
export function gstFromInclusive(inclusive: Money): { net: Money; gst: Money } {
  const parts = allocate(inclusive, [GST_NUMERATOR, GST_INCLUSIVE_DENOMINATOR - GST_NUMERATOR]);
  return { net: share(parts, 1), gst: share(parts, 0) };
}

/** GST to add to a GST-exclusive amount. exclusive * 3 / 20. */
export function gstFromExclusive(exclusive: Money): { gross: Money; gst: Money } {
  // Work on the magnitude, as `allocate` does: bigint division truncates toward zero, so rounding
  // a negative directly would round a credit's tax the other way from the charge it reverses.
  const negative = exclusive < ZERO;
  const magnitude = negative ? -exclusive : exclusive;
  const gst = (magnitude * GST_NUMERATOR) / GST_EXCLUSIVE_DENOMINATOR;
  const remainder = (magnitude * GST_NUMERATOR) % GST_EXCLUSIVE_DENOMINATOR;
  // Round half up on the tax line — the conventional treatment — and away from zero for a credit.
  const rounded = remainder * 2n >= GST_EXCLUSIVE_DENOMINATOR ? gst + 1n : gst;
  const signed = negative ? -rounded : rounded;
  return { gross: exclusive + signed, gst: signed };
}

/**
 * Apply a rate expressed in basis points (1 bp = 0.01%). Commission of 8% is 800n.
 * Basis points, not floats, so an 8% fee is exactly 8% every time.
 */
export function applyBasisPoints(amount: Money, basisPoints: bigint): Money {
  return share(allocate(amount, [basisPoints, 10_000n - basisPoints]), 0);
}

export const sum = (amounts: readonly Money[]): Money => amounts.reduce((a, b) => a + b, ZERO);
export const isZero = (a: Money): boolean => a === ZERO;
export const isNegative = (a: Money): boolean => a < ZERO;
export const abs = (a: Money): Money => (a < ZERO ? -a : a);
export const max = (a: Money, b: Money): Money => (a > b ? a : b);
export const min = (a: Money, b: Money): Money => (a < b ? a : b);
